import { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Check, Copy, Lightbulb, ChevronRight, Pin, PinOff } from 'lucide-react';

function getPriorityStyle(priority) {
  switch (priority) {
    case 'high':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'medium':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    default:
      return 'bg-zinc-50 text-zinc-700 border-zinc-200';
  }
}

export function SuggestedQuestions({ questions, gaps, onQuestionAsked, pinnedQuestion, onPinQuestion }) {
  const [askedQuestions, setAskedQuestions] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  const safeQuestions = questions || [];
  const safeGaps = gaps || [];

  // If a pinned question exists but isn't in the latest analysis output, we
  // still render it at the top so the rep doesn't lose it between cycles.
  const pinnedText = pinnedQuestion?.question || pinnedQuestion?.text;
  const orderedQuestions = (() => {
    if (!pinnedText) return safeQuestions;
    const rest = safeQuestions.filter(q => (q.question || q.text) !== pinnedText);
    // Use whatever shape the pinned item is in (full object preferred for category/reason).
    const head = safeQuestions.find(q => (q.question || q.text) === pinnedText) || pinnedQuestion;
    return [head, ...rest];
  })();

  const handleAsk = (questionId) => {
    setAskedQuestions(prev => new Set([...prev, questionId]));
    if (onQuestionAsked) {
      onQuestionAsked(questionId);
    }
  };

  const handleCopy = async (question, id) => {
    try {
      await navigator.clipboard.writeText(question);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="suggested-questions">
      {/* Gaps Section */}
      {safeGaps.length > 0 && (
        <div className="mb-6">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
            Areas Needing Attention
          </span>
          <div className="space-y-2">
            {safeGaps.map((gap, index) => (
              <div 
                key={index}
                className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-md p-2"
                data-testid={`gap-item-${index}`}
              >
                <ChevronRight className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-red-700">{gap}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Questions Section */}
      <div className="flex-1">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
          Suggested Questions
        </span>
        
        {orderedQuestions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="no-questions">
            <Lightbulb className="w-8 h-8 text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">
              Questions will appear here based on your conversation
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100%-2rem)]">
            <div className="space-y-3 pr-2">
              {orderedQuestions.map((item, index) => {
                const itemText = item.question || item.text || '';
                const isAsked = askedQuestions.has(itemText);
                const isCopied = copiedId === itemText;
                const isPinned = pinnedText && itemText === pinnedText;
                
                return (
                  <div
                    key={index}
                    className={`question-card border rounded-md p-3 transition-all ${
                      isPinned
                        ? 'bg-amber-50 border-amber-300 shadow-sm'
                        : 'bg-white border-zinc-200'
                    } ${isAsked ? 'opacity-50' : 'card-hover'}`}
                    data-testid={`suggested-question-${index}`}
                  >
                    {isPinned && (
                      <div className="flex items-center gap-1.5 mb-2" data-testid="pinned-question-badge">
                        <Pin className="w-3 h-3 text-amber-700 fill-amber-600" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-700">
                          Pinned
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getPriorityStyle(item.priority)}`}
                      >
                        {item.category}
                      </Badge>
                      <Badge 
                        variant="outline"
                        className={`text-xs capitalize ${
                          item.priority === 'high' ? 'bg-red-50 text-red-600 border-red-200' :
                          item.priority === 'medium' ? 'bg-yellow-50 text-yellow-600 border-yellow-200' :
                          'bg-zinc-50 text-zinc-600 border-zinc-200'
                        }`}
                      >
                        {item.priority}
                      </Badge>
                    </div>
                    
                    <p className={`text-sm mb-2 ${isAsked ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>
                      {itemText}
                    </p>
                    
                    {item.reason && (
                      <p className="text-xs text-zinc-500 mb-3 italic">
                        {item.reason}
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={isAsked ? "ghost" : "default"}
                        className={`flex-1 h-8 text-xs rounded-md ${
                          isAsked 
                            ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                            : 'bg-brand-primary hover:bg-brand-hover text-white'
                        }`}
                        onClick={() => handleAsk(itemText)}
                        disabled={isAsked}
                        data-testid={`mark-asked-btn-${index}`}
                      >
                        {isAsked ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Asked
                          </>
                        ) : (
                          'Mark as Asked'
                        )}
                      </Button>
                      {onPinQuestion && (
                        <Button
                          size="sm"
                          variant="outline"
                          className={`h-8 w-8 p-0 rounded-md ${
                            isPinned ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200' : ''
                          }`}
                          title={isPinned ? 'Unpin question' : 'Pin to top'}
                          onClick={() => onPinQuestion(isPinned ? null : item)}
                          data-testid={`pin-question-btn-${index}`}
                        >
                          {isPinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 rounded-md"
                        onClick={() => handleCopy(itemText, itemText)}
                        data-testid={`copy-question-btn-${index}`}
                      >
                        {isCopied ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
